import { LandingNav } from "@/components/landing/LandingNav";
import { SEO } from "@/components/SEO";
import { LandingFooter } from "@/components/landing/LandingFooter";

const Privacy = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Privacy Policy — RaffleKit"
                description="RaffleKit privacy policy."
                canonical="https://rafflekit.app/privacy"
                noIndex={true}
            />
            <LandingNav />

            <section className="pt-32 pb-20 bg-gray-50">
                <div className="container mx-auto px-4 max-w-4xl text-left">
                    <h1 className="font-heading text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
                    <p className="text-gray-500 mb-12">Last updated: {new Date().toLocaleDateString()}</p>

                    <div className="prose prose-lg prose-gray max-w-none space-y-8">

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Company Identity</h2>
                            <p>
                                RaffleKit is an operating name of GKNS Holdings Inc., a California corporation (“RaffleKit,” “Company,” “we,” “us,” or “our”). This Privacy Policy describes how we collect, use, disclose, and safeguard information when you use our website, applications, and related services (collectively, the “Service”).
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Scope & Roles</h2>
                            <p>
                                RaffleKit provides software tools that allow businesses and individuals (“Sponsors”) to create and manage contests and giveaways.
                            </p>
                            <p>
                                For Sponsor account data, RaffleKit acts as a data controller.
                                For participant data collected through contests, RaffleKit generally acts as a data processor on behalf of the Sponsor. The Sponsor is responsible for ensuring they have the lawful right to collect and process participant data.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information We Collect</h2>

                            <h3>Information You Provide</h3>
                            <ul>
                                <li>Name and email address</li>
                                <li>Account credentials</li>
                                <li>Contest details and content</li>
                                <li>Support communications</li>
                                <li>Billing and subscription information</li>
                            </ul>

                            <h3>Participant Information</h3>
                            <ul>
                                <li>Email address</li>
                                <li>Referral codes and referral relationships</li>
                                <li>Entry method activity</li>
                                <li>IP address hash and device hash (fraud prevention)</li>
                            </ul>

                            <h3>Automatically Collected Information</h3>
                            <ul>
                                <li>Log data (IP address, browser type, device information)</li>
                                <li>Usage data and activity logs</li>
                                <li>Security and abuse-prevention data</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Use Information</h2>
                            <p>We use collected information to:</p>
                            <ul>
                                <li>Operate and maintain the Service</li>
                                <li>Facilitate contest entry collection and winner selection</li>
                                <li>Enforce plan limits and subscription terms</li>
                                <li>Detect and prevent fraud or abuse</li>
                                <li>Provide customer support</li>
                                <li>Improve product features and performance</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Legal Bases for Processing</h2>
                            <p>
                                Where required by law, we process personal information under the following legal bases:
                            </p>
                            <ul>
                                <li>Performance of a contract</li>
                                <li>Legitimate business interests</li>
                                <li>Compliance with legal obligations</li>
                                <li>Consent (where applicable)</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sharing & Disclosure</h2>
                            <p>We do not sell personal information.</p>
                            <p>We may share information with:</p>
                            <ul>
                                <li>Cloud hosting providers</li>
                                <li>Payment processors</li>
                                <li>Email delivery services</li>
                                <li>Analytics providers (if implemented in the future)</li>
                                <li>Legal authorities where required</li>
                            </ul>
                            <p>
                                All service providers are contractually bound to maintain confidentiality and appropriate safeguards.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
                            <p>
                                We retain account data for as long as necessary to provide the Service and comply with legal obligations. Contest participant data is retained based on Sponsor account status and operational requirements.
                            </p>
                            <p>
                                We may retain limited data for fraud prevention, dispute resolution, or legal compliance even after account closure.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Security</h2>
                            <p>
                                We implement reasonable administrative, technical, and organizational safeguards to protect information. These include encrypted data transmission, secure infrastructure hosting, access controls, and abuse monitoring systems.
                            </p>
                            <p>
                                However, no internet-based service can guarantee absolute security.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies & Tracking Technologies</h2>
                            <p>
                                We use cookies and similar technologies to maintain session state, secure accounts, and improve performance. In the future, we may implement analytics tools such as Google Analytics.
                            </p>
                            <p>
                                You can manage cookie settings through your browser preferences.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. California Privacy Rights (CCPA/CPRA)</h2>
                            <p>
                                If you are a California resident, you may have rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), including:
                            </p>
                            <ul>
                                <li>The right to know what personal information we collect</li>
                                <li>The right to request deletion of personal information</li>
                                <li>The right to correct inaccurate information</li>
                                <li>The right to limit use of sensitive personal information</li>
                                <li>The right to non-discrimination for exercising privacy rights</li>
                            </ul>
                            <p>
                                To exercise these rights, contact us at hello@rafflekit.app.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Users</h2>
                            <p>
                                Our Service is hosted in the United States. By using the Service from outside the U.S., you consent to the transfer and processing of your information in the United States.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Children’s Privacy</h2>
                            <p>
                                The Service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. Sponsors are responsible for setting age eligibility requirements for contests.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy periodically. Changes will be posted on this page with an updated effective date.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
                            <p>
                                RaffleKit (Operating name of GKNS Holdings Inc.)<br />
                                1968 S. Coast Hwy, Suite 3200, Laguna Beach, CA 92651, United States<br />
                                hello@rafflekit.app
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default Privacy;